import React, { useEffect, useMemo, useRef, useState } from "react";
import { CompassRose } from "./CompassRose";
import { toast } from "sonner";
import { PlusSquare, Grid2x2, Undo2, Trash2, Pencil, Crosshair, PenTool, MousePointer2, RotateCcw, RotateCw, X, ZoomIn, ZoomOut, Compass, AlignHorizontalJustifyCenter } from "lucide-react";

const DEG = Math.PI / 180;

function pip(px, pz, verts) {
  let c = false;
  for (let i = 0, j = verts.length - 1; i < verts.length; j = i++) {
    const xi = verts[i].x, zi = verts[i].z, xj = verts[j].x, zj = verts[j].z;
    if ((zi > pz) !== (zj > pz) && px < ((xj - xi) * (pz - zi)) / (zj - zi) + xi) c = !c;
  }
  return c;
}
function polyArea(verts) {
  let a = 0;
  for (let i = 0, j = verts.length - 1; i < verts.length; j = i++) a += (verts[j].x + verts[i].x) * (verts[j].z - verts[i].z);
  return Math.abs(a / 2);
}
function angleOf(poly) {
  const v = poly.verts;
  return Math.atan2(v[1].z - v[0].z, v[1].x - v[0].x);
}

export default function RoofEditor2D({
  imageUrl, span = 80, panelW = 1.05, panelH = 1.9, moduleWp = 0, domPitch = 20, maxPanels = 0,
  polys, setPolys, panels, setPanels, onPickBuilding, rotation = 0, onRotationChange, faldaAz, lat, lng,
}) {
  const svgRef = useRef(null);
  const worldRef = useRef(null);
  const [rotLocal, setRotLocal] = useState(rotation);
  const rot = onRotationChange ? rotation : rotLocal;
  const setRot = (v) => {
    const next = typeof v === "function" ? v(rot) : v;
    (onRotationChange || setRotLocal)(next);
  };
  const [showView, setShowView] = useState(() => window.innerWidth >= 768);
  const fitPx = () => Math.min(svgRef.current?.clientWidth || 900, svgRef.current?.clientHeight || 900) || 900;
  const [vb, setVb] = useState({ x: -span / 2, y: -span / 2, w: span, h: span });
  const [tool, setTool] = useState("place"); // place | panel | edit | draw | pick
  const [selId, setSelId] = useState(polys?.[0]?.id || null);
  const [selPanel, setSelPanel] = useState(-1);
  const [drawPts, setDrawPts] = useState([]);
  const [snap, setSnap] = useState(true);
  const drag = useRef(null);
  const livePos = useRef(null);
  const moved = useRef(false);
  const downPt = useRef(null);

  const sel = polys.find((p) => p.id === selId) || null;
  const fontSize = vb.w * 0.03;
  const handleR = vb.w * (window.innerWidth < 768 ? 0.018 : 0.009);
  const fmtCm = (m) => `${Math.round(m * 100)} cm`;
  const dCos = panelH * Math.cos(20 * DEG);
  const kwp = Math.round((panels.length * (moduleWp || 0)) / 10) / 100;

  // Coordinate "mondo" (metri, nord in alto) tenendo conto della rotazione della vista
  const clientToSvg = (e) => {
    const svg = svgRef.current;
    const pt = svg.createSVGPoint();
    pt.x = e.clientX; pt.y = e.clientY;
    const p = pt.matrixTransform((worldRef.current || svg).getScreenCTM().inverse());
    return { x: p.x, z: p.y };
  };
  const clientToView = (e) => {
    const svg = svgRef.current;
    const pt = svg.createSVGPoint();
    pt.x = e.clientX; pt.y = e.clientY;
    const p = pt.matrixTransform(svg.getScreenCTM().inverse());
    return { x: p.x, z: p.y };
  };
  const norm = (d) => ((Math.round(d) % 360) + 540) % 360 - 180;
  const alignToFalda = () => {
    const p = sel || polys[0];
    if (!p) { toast.error("Seleziona una falda da allineare"); return; }
    let best = 0, ang = 0;
    p.verts.forEach((v, i) => {
      const n = p.verts[(i + 1) % p.verts.length];
      const len = Math.hypot(n.x - v.x, n.z - v.z);
      if (len > best) { best = len; ang = (Math.atan2(n.z - v.z, n.x - v.x) * 180) / Math.PI; }
    });
    let r = norm(-ang);
    if (r > 90) r -= 180; if (r < -90) r += 180;
    setRot(r);
    toast.success(`Vista ruotata di ${r}°: lato lungo della falda in orizzontale`);
  };

  // Reset della vista quando cambia la copertura a terra (nuovo indirizzo/edificio)
  useEffect(() => { setVb({ x: -span / 2, y: -span / 2, w: span, h: span }); }, [span]);

  // Zoom-in massimo = risoluzione nativa della satellitare (Google cappa il raster a zoom 21,
  // ≈1280 px sull'intera copertura `span`). Oltre ~2× di ingrandimento sarebbe solo sfocatura.
  // Zoom massimo: ~8 px schermo per pixel nativo della foto (10 cm) → si vedono bene coppi e bordi.
  const minViewWidth = () => {
    return Math.max(3, fitPx() / (10 * 8));
  };
  const zoomAt = (cx, cz, f) => {
    const nw = Math.min(span * 3, Math.max(minViewWidth(), vb.w * f));
    const k = nw / vb.w;
    setVb({ x: cx - (cx - vb.x) * k, y: cz - (cz - vb.y) * k, w: nw, h: vb.h * k });
  };
  const zoomBtn = (f) => zoomAt(vb.x + vb.w / 2, vb.y + vb.h / 2, f);

  const onWheel = (e) => {
    e.preventDefault();
    const sp = clientToView(e);
    zoomAt(sp.x, sp.z, e.deltaY > 0 ? 1.12 : 0.89);
  };
  const wheelRef = useRef(onWheel);
  wheelRef.current = onWheel;
  useEffect(() => {
    const el = svgRef.current;
    const h = (e) => wheelRef.current(e);
    el.addEventListener("wheel", h, { passive: false });
    return () => el.removeEventListener("wheel", h);
  }, []);

  const onDownBg = (e) => {
    moved.current = false;
    downPt.current = clientToSvg(e);
    drag.current = { type: "pan", sx: e.clientX, sy: e.clientY, vx: vb.x, vy: vb.y };
  };
  const onMove = (e) => {
    const d = drag.current;
    if (!d) return;
    if (Math.abs(e.clientX - d.sx) + Math.abs(e.clientY - d.sy) > 3) moved.current = true;
    if (d.type === "pan") {
      const scale = vb.w / fitPx();
      setVb((v) => ({ ...v, x: d.vx - (e.clientX - d.sx) * scale, y: d.vy - (e.clientY - d.sy) * scale }));
    } else if (d.type === "vertex") {
      const sp = clientToSvg(e); const { polyId, idx } = d;
      setPolys((prev) => prev.map((p) => p.id !== polyId ? p : { ...p, verts: p.verts.map((vt, i) => i === idx ? { x: sp.x, z: sp.z } : vt) }));
    } else if (d.type === "panelmove") {
      const sp = clientToSvg(e); const idx = d.idx;
      let np = { x: sp.x, z: sp.z, az: null };
      if (snap) { const poly = polys.find((p) => pip(sp.x, sp.z, p.verts)); if (poly) np = snapInPoly(poly, sp.x, sp.z); }
      livePos.current = { x: np.x, z: np.z };
      setPanels((prev) => prev.map((pn, i) => i === idx ? { ...pn, x: np.x, z: np.z, ...(np.az != null ? { az: np.az } : {}) } : pn));
    } else if (d.type === "panelrot") {
      const sp = clientToSvg(e); const idx = d.idx;
      setPanels((prev) => prev.map((p, i) => { if (i !== idx) return p; const az = (Math.atan2(sp.z - p.z, sp.x - p.x) * 180) / Math.PI + 90; return { ...p, az }; }));
    }
  };
  const onUp = () => {
    const d = drag.current;
    drag.current = null;
    if (d?.type === "panelmove" && d.start) {
      const pn = livePos.current || panels[d.idx];
      livePos.current = null;
      if (pn && !polys.some((p) => pip(pn.x, pn.z, p.verts))) {
        setPanels((prev) => prev.map((p, i) => i === d.idx ? { ...p, x: d.start.x, z: d.start.z } : p));
        setTimeout(() => toast.error("Modulo fuori dal tetto: riportato dentro"), 0);
        return;
      }
    }
    if (!d || moved.current) return;
    const sp = downPt.current;
    if (tool === "pick") { onPickBuilding && onPickBuilding(sp); return; }
    if (tool === "draw") {
      if (drawPts.length >= 3 && Math.hypot(sp.x - drawPts[0].x, sp.z - drawPts[0].z) < vb.w * 0.02) { finalizeDraw(); return; }
      setDrawPts((p) => [...p, { x: sp.x, z: sp.z }]);
      return;
    }
    if (tool === "panel") { if (d.type === "pan") setSelPanel(-1); return; }
    if (tool === "place") {
      const target = polys.find((p) => pip(sp.x, sp.z, p.verts));
      if (!target) { setTimeout(() => toast.error("Fuori dal tetto: clicca dentro una falda"), 0); return; }
      const wp = moduleWp || 0;
      const pos = snap ? snapInPoly(target, sp.x, sp.z) : { x: sp.x, z: sp.z, az: (angleOf(target) * 180) / Math.PI };
      const n = panels.length + 1;
      setPanels((prev) => [...prev, { x: pos.x, z: pos.z, az: pos.az, pitch: target.pitch }]);
      toast.success(`Modulo +${wp} W · ${n} moduli · ${((n * wp) / 1000).toFixed(2)} kWp`);
    }
  };

  const finalizeDraw = () => {
    if (drawPts.length < 3) { toast.error("Servono almeno 3 punti"); return; }
    const az = (Math.atan2(drawPts[1].z - drawPts[0].z, drawPts[1].x - drawPts[0].x) * 180) / Math.PI;
    const id = `d${Date.now()}`;
    setPolys((prev) => [...prev, { id, segIndex: -1, az, pitch: domPitch || 20, verts: drawPts }]);
    setDrawPts([]); setSelId(id); setTool("place");
    toast.success("Falda disegnata");
  };

  const onVertexDown = (e, polyId, idx) => { e.stopPropagation(); setSelId(polyId); drag.current = { type: "vertex", polyId, idx, sx: e.clientX, sy: e.clientY }; moved.current = false; };
  const onVertexDbl = (e, polyId) => {
    e.stopPropagation();
    setPolys((prev) => prev.map((p) => (p.id !== polyId || p.verts.length <= 3) ? p : { ...p, verts: p.verts.filter((_, i) => i !== e._idx) }));
  };
  const onEdgeDown = (e, polyId, i) => {
    e.stopPropagation();
    const sp = clientToSvg(e);
    setPolys((prev) => prev.map((p) => {
      if (p.id !== polyId) return p;
      const verts = [...p.verts]; verts.splice(i + 1, 0, { x: sp.x, z: sp.z }); return { ...p, verts };
    }));
    setSelId(polyId); drag.current = { type: "vertex", polyId, idx: i + 1, sx: e.clientX, sy: e.clientY }; moved.current = false;
  };
  const onPanelDown = (e, i) => {
    e.stopPropagation();
    setSelPanel(i); setTool("panel");
    drag.current = { type: "panelmove", idx: i, sx: e.clientX, sy: e.clientY, start: { x: panels[i].x, z: panels[i].z } };
    moved.current = false;
  };
  const onRotDown = (e, i) => { e.stopPropagation(); drag.current = { type: "panelrot", idx: i, sx: e.clientX, sy: e.clientY }; moved.current = false; };

  const deleteFalda = () => {
    if (!sel) { toast.error("Seleziona una falda"); return; }
    setPolys((prev) => prev.filter((p) => p.id !== sel.id));
    setPanels((prev) => prev.filter((pn) => !pip(pn.x, pn.z, sel.verts)));
    setSelId(null);
    toast.success("Falda eliminata");
  };
  const rotateSel = (deg) => setPanels((prev) => prev.map((p, i) => i === selPanel ? { ...p, az: (p.az || 0) + deg } : p));
  const deleteSelPanel = () => { setPanels((prev) => prev.filter((_, i) => i !== selPanel)); setSelPanel(-1); };

  const autoFill = () => {
    const targets = sel ? [sel] : polys;
    if (!targets.length) { toast.error("Nessuna falda da riempire"); return; }
    const cap = maxPanels > 0 ? maxPanels : Infinity;
    let next = [...panels];
    targets.forEach((poly) => { next = next.filter((p) => !pip(p.x, p.z, poly.verts)); });
    let added = 0;
    outer:
    for (const poly of targets) {
      const a = angleOf(poly), ca = Math.cos(a), sa = Math.sin(a);
      const u = { x: ca, z: sa }, v = { x: -sa, z: ca };
      const uw = panelW, vh = panelH * Math.cos((poly.pitch || 20) * DEG), gap = 0.05;
      const proj = poly.verts.map((p) => ({ u: p.x * u.x + p.z * u.z, v: p.x * v.x + p.z * v.z }));
      const minU = Math.min(...proj.map((p) => p.u)), maxU = Math.max(...proj.map((p) => p.u));
      const minV = Math.min(...proj.map((p) => p.v)), maxV = Math.max(...proj.map((p) => p.v));
      for (let cu = minU + uw / 2; cu <= maxU - uw / 2 + 1e-6; cu += uw + gap) {
        for (let cv = minV + vh / 2; cv <= maxV - vh / 2 + 1e-6; cv += vh + gap) {
          if (added >= cap) break outer;
          const cx = cu * u.x + cv * v.x, cz = cu * u.z + cv * v.z;
          const corners = [[uw / 2, vh / 2], [-uw / 2, vh / 2], [uw / 2, -vh / 2], [-uw / 2, -vh / 2]];
          const ok = corners.every(([du, dv]) => pip((cu + du) * u.x + (cv + dv) * v.x, (cu + du) * u.z + (cv + dv) * v.z, poly.verts));
          if (ok) { next.push({ x: cx, z: cz, az: (a * 180) / Math.PI, pitch: poly.pitch }); added++; }
        }
      }
    }
    setPanels(next);
    toast.success(maxPanels > 0 ? `${added} moduli inseriti (obiettivo ${maxPanels})` : `${added} moduli inseriti automaticamente`);
  };

  const perFalda = useMemo(() => polys.map((p, i) => ({ id: p.id, i, area: polyArea(p.verts), count: panels.filter((pn) => pip(pn.x, pn.z, p.verts)).length })), [polys, panels]);

  // Aggancio alla griglia della falda (stesso passo del riempimento automatico)
  const snapInPoly = (poly, px, pz) => {
    const a = angleOf(poly), ca = Math.cos(a), sa = Math.sin(a);
    const u = { x: ca, z: sa }, v = { x: -sa, z: ca };
    const uw = panelW, vh = panelH * Math.cos((poly.pitch || 20) * DEG), gap = 0.05;
    const proj = poly.verts.map((p) => ({ u: p.x * u.x + p.z * u.z, v: p.x * v.x + p.z * v.z }));
    const minU = Math.min(...proj.map((p) => p.u)), minV = Math.min(...proj.map((p) => p.v));
    const cu0 = minU + uw / 2, cv0 = minV + vh / 2, stepU = uw + gap, stepV = vh + gap;
    const pu = px * u.x + pz * u.z, pv = px * v.x + pz * v.z;
    const cu = cu0 + Math.round((pu - cu0) / stepU) * stepU;
    const cv = cv0 + Math.round((pv - cv0) / stepV) * stepV;
    return { x: cu * u.x + cv * v.x, z: cu * u.z + cv * v.z, az: (a * 180) / Math.PI };
  };

  const btn = (active) => active ? "btn-primary" : "btn-secondary";
  const [mobile, setMobile] = useState(() => window.matchMedia("(max-width: 767px)").matches);
  const [moreOpen, setMoreOpen] = useState(false);
  useEffect(() => {
    const q = window.matchMedia("(max-width: 767px)");
    const h = (e) => setMobile(e.matches);
    q.addEventListener("change", h);
    return () => q.removeEventListener("change", h);
  }, []);

  // Pizzico con due dita: zoom attorno al centro delle dita + pan
  const ptrs = useRef(new Map());
  const pinch = useRef(null);
  const pinchInfo = () => {
    const [a, b] = [...ptrs.current.values()];
    return { d: Math.hypot(a.x - b.x, a.y - b.y) || 1, cx: (a.x + b.x) / 2, cy: (a.y + b.y) / 2 };
  };
  const onPtrDownCap = (e) => {
    ptrs.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (ptrs.current.size === 2) {
      const i = pinchInfo();
      const c = clientToView({ clientX: i.cx, clientY: i.cy });
      pinch.current = { ...i, c, vb };
      drag.current = { type: "pinch" };
      moved.current = true;
    }
  };
  const onPtrMoveCap = (e) => {
    if (!ptrs.current.has(e.pointerId)) return;
    ptrs.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    const p = pinch.current;
    if (!p || ptrs.current.size < 2) return;
    e.stopPropagation();
    const i = pinchInfo();
    const nw = Math.min(span * 3, Math.max(minViewWidth(), p.vb.w * (p.d / i.d)));
    const k = nw / p.vb.w, s = nw / fitPx();
    setVb({ x: p.c.x - (p.c.x - p.vb.x) * k - (i.cx - p.cx) * s, y: p.c.z - (p.c.z - p.vb.y) * k - (i.cy - p.cy) * s, w: nw, h: p.vb.h * k });
  };
  const onPtrUpCap = (e) => {
    ptrs.current.delete(e.pointerId);
    if (pinch.current) {
      if (ptrs.current.size < 2) { pinch.current = null; drag.current = null; moved.current = true; }
      e.stopPropagation();
    }
  };
  const undoPoint = () => setDrawPts((p) => p.slice(0, -1));
  const more = [
    ["roof-del-falda", <Trash2 className="w-5 h-5" />, "Elimina falda", deleteFalda],
    ["roof-autofill", <Grid2x2 className="w-5 h-5" />, "Riempi", autoFill],
    ["roof-undo", <Undo2 className="w-5 h-5" />, "Rimuovi ultimo", () => setPanels((p) => p.slice(0, -1))],
    ["roof-clear", <Trash2 className="w-5 h-5" />, "Svuota moduli", () => setPanels([])],
    ["roof-pick", <Crosshair className="w-5 h-5" />, "Seleziona edificio", () => setTool("pick")],
    ["roof-snap-toggle", <Grid2x2 className="w-5 h-5" />, snap ? "Aggancio ON" : "Aggancio OFF", () => setSnap((s) => !s)],
    ["roof-view-toggle", <Compass className="w-5 h-5" />, showView ? "Nascondi rotazione" : "Rotazione vista", () => setShowView((v) => !v)],
    ["roof-align-falda-m", <AlignHorizontalJustifyCenter className="w-5 h-5" />, "Allinea alla falda", alignToFalda],
  ];
  const dockBtn = (id, icon, label, active, onClick) => (
    <button key={id} onClick={onClick} data-testid={id} className="flex-1 flex flex-col items-center justify-center gap-0.5 py-2 rounded-lg text-[11px] font-semibold min-w-0"
      style={{ background: active ? "var(--primary)" : "transparent", color: active ? "#fff" : "#1e293b" }}>{icon}<span className="truncate max-w-full">{label}</span></button>
  );
  const ctxBar = tool === "draw" ? (
    <>
      <span className="text-xs font-semibold px-1 self-center" style={{ color: "#92400e" }}>{drawPts.length ? `${drawPts.length} punti` : "Tocca gli angoli della falda"}</span>
      {drawPts.length > 0 && <button onClick={undoPoint} className="btn-secondary !py-2" data-testid="roof-draw-undo-point"><Undo2 className="w-4 h-4" /> Punto</button>}
      {drawPts.length >= 3 && <button onClick={finalizeDraw} className="btn-primary !py-2" data-testid="roof-draw-close">✓ Chiudi falda</button>}
      {drawPts.length > 0 && <button onClick={() => setDrawPts([])} className="btn-secondary !py-2" data-testid="roof-draw-cancel"><X className="w-4 h-4" /> Tutto</button>}
    </>
  ) : tool === "panel" && selPanel >= 0 ? (
    <>
      <button onClick={() => rotateSel(-15)} className="btn-secondary !py-2" data-testid="panel-rot-ccw"><RotateCcw className="w-4 h-4" /> -15°</button>
      <button onClick={() => rotateSel(15)} className="btn-secondary !py-2" data-testid="panel-rot-cw"><RotateCw className="w-4 h-4" /> +15°</button>
      <button onClick={deleteSelPanel} className="btn-secondary !py-2" data-testid="panel-del"><Trash2 className="w-4 h-4" /> Elimina</button>
    </>
  ) : tool === "pick" ? <span className="text-xs font-semibold px-1 py-2" data-testid="roof-pick-hint">Tocca l'edificio sulla mappa</span> : null;

  return (
    <div style={{ position: "relative", width: "100%", height: "100%" }}>
      {!mobile && <div className="absolute top-2 left-2 right-2 md:right-auto z-[6] flex items-start gap-1.5 flex-nowrap overflow-x-auto md:flex-wrap md:overflow-visible md:max-w-[calc(100%-216px)] pb-1 [&>*]:shrink-0 [&>*]:whitespace-nowrap" data-testid="roof-toolbar">
        <button onClick={() => { setTool("place"); setSelPanel(-1); }} className={btn(tool === "place")} data-testid="roof-tool-place"><PlusSquare className="w-4 h-4" /> Posiziona</button>
        <button onClick={() => setTool("panel")} className={btn(tool === "panel")} data-testid="roof-tool-panel"><MousePointer2 className="w-4 h-4" /> Moduli</button>
        <button onClick={() => { setTool("edit"); setSelPanel(-1); }} className={btn(tool === "edit")} data-testid="roof-tool-edit"><Pencil className="w-4 h-4" /> Modifica bordi</button>
        <button onClick={() => { setTool("draw"); setDrawPts([]); setSelPanel(-1); }} className={btn(tool === "draw")} data-testid="roof-tool-draw"><PenTool className="w-4 h-4" /> Disegna falda</button>
        <button onClick={deleteFalda} className="btn-secondary" data-testid="roof-del-falda"><Trash2 className="w-4 h-4" /> Elimina falda</button>
        <button onClick={autoFill} className="btn-secondary" data-testid="roof-autofill"><Grid2x2 className="w-4 h-4" /> Riempi</button>
        <button onClick={() => setPanels((p) => p.slice(0, -1))} className="btn-secondary" data-testid="roof-undo"><Undo2 className="w-4 h-4" /> Rimuovi ultimo</button>
        <button onClick={() => setPanels([])} className="btn-secondary" data-testid="roof-clear"><Trash2 className="w-4 h-4" /> Svuota moduli</button>
        <button onClick={() => setTool("pick")} className={btn(tool === "pick")} data-testid="roof-pick"><Crosshair className="w-4 h-4" /> Seleziona edificio</button>
        <button onClick={() => setSnap((s) => !s)} className={btn(snap)} data-testid="roof-snap-toggle"><Grid2x2 className="w-4 h-4" /> {snap ? "Aggancio ON" : "Aggancio OFF"}</button>
        {tool === "draw" && drawPts.length >= 3 && (
          <button onClick={finalizeDraw} className="btn-primary" data-testid="roof-draw-close">✓ Chiudi falda</button>
        )}
        {tool === "draw" && drawPts.length > 0 && (
          <button onClick={() => setDrawPts([])} className="btn-secondary" data-testid="roof-draw-cancel"><X className="w-4 h-4" /> Annulla</button>
        )}
        {tool === "panel" && selPanel >= 0 && (
          <>
            <button onClick={() => rotateSel(-15)} className="btn-secondary" data-testid="panel-rot-ccw"><RotateCcw className="w-4 h-4" /> -15°</button>
            <button onClick={() => rotateSel(15)} className="btn-secondary" data-testid="panel-rot-cw"><RotateCw className="w-4 h-4" /> +15°</button>
            <button onClick={deleteSelPanel} className="btn-secondary" data-testid="panel-del"><Trash2 className="w-4 h-4" /> Elimina modulo</button>
          </>
        )}
        <span className="text-sm font-bold px-3 py-1.5 rounded-md" style={{ background: "#ffffffcc", color: "var(--primary)" }} data-testid="roof-power">{panels.length} moduli · {kwp} kWp</span>
      </div>}

      {mobile && (
        <>
          <span className="absolute top-2 left-2 z-[6] text-xs font-bold px-2.5 py-1.5 rounded-md shadow" style={{ background: "#ffffffeb", color: "var(--primary)" }} data-testid="roof-power">{panels.length} moduli · {kwp} kWp</span>
          <div className="absolute right-2 z-[6] flex flex-col gap-2" style={{ bottom: ctxBar ? 132 : 84 }} data-testid="roof-zoom-mobile">
            <button onClick={() => zoomBtn(0.7)} className="w-11 h-11 rounded-full shadow flex items-center justify-center" style={{ background: "#ffffffeb" }} data-testid="roof-zoom-in-m"><ZoomIn className="w-5 h-5" /></button>
            <button onClick={() => zoomBtn(1.4)} className="w-11 h-11 rounded-full shadow flex items-center justify-center" style={{ background: "#ffffffeb" }} data-testid="roof-zoom-out-m"><ZoomOut className="w-5 h-5" /></button>
            <button onClick={() => setVb({ x: -span / 2, y: -span / 2, w: span, h: span })} className="w-11 h-11 rounded-full shadow flex items-center justify-center" style={{ background: "#ffffffeb" }} data-testid="roof-view-reset-m"><Crosshair className="w-5 h-5" /></button>
          </div>
          {ctxBar && <div className="absolute left-2 right-2 bottom-[76px] z-[7] flex gap-1.5 overflow-x-auto p-1.5 rounded-xl shadow [&>*]:shrink-0 [&>*]:whitespace-nowrap" style={{ background: tool === "draw" ? "#FEF3C7f2" : "#ffffffeb" }} data-testid="roof-ctx-bar">{ctxBar}</div>}
          <div className="absolute left-2 right-2 bottom-2 z-[7] flex gap-1 p-1 rounded-xl shadow-lg" style={{ background: "#fffffff5" }} data-testid="roof-dock">
            {dockBtn("roof-tool-draw", <PenTool className="w-5 h-5" />, "Disegna", tool === "draw", () => { setTool("draw"); setDrawPts([]); setSelPanel(-1); })}
            {dockBtn("roof-tool-edit", <Pencil className="w-5 h-5" />, "Modifica", tool === "edit", () => { setTool("edit"); setSelPanel(-1); })}
            {dockBtn("roof-tool-place", <PlusSquare className="w-5 h-5" />, "Posiziona", tool === "place", () => { setTool("place"); setSelPanel(-1); })}
            {dockBtn("roof-tool-panel", <MousePointer2 className="w-5 h-5" />, "Moduli", tool === "panel", () => setTool("panel"))}
            {dockBtn("roof-more", <Grid2x2 className="w-5 h-5" />, "Altro", moreOpen, () => setMoreOpen(true))}
          </div>
          {moreOpen && (
            <div className="absolute inset-0 z-[9] flex items-end" style={{ background: "rgba(0,0,0,.4)" }} onClick={() => setMoreOpen(false)} data-testid="roof-more-sheet">
              <div className="w-full bg-white rounded-t-2xl p-3 grid grid-cols-2 gap-2" onClick={(e) => e.stopPropagation()}>
                {more.map(([id, icon, label, fn]) => (
                  <button key={id} onClick={() => { fn(); setMoreOpen(false); }} className="btn-secondary justify-start !py-3" data-testid={id}>{icon} {label}</button>
                ))}
                <button onClick={() => setMoreOpen(false)} className="col-span-2 py-2 text-sm" style={{ color: "var(--text-muted)" }} data-testid="roof-more-close">Chiudi</button>
              </div>
            </div>
          )}
        </>
      )}

      <svg ref={svgRef} viewBox={`${vb.x} ${vb.y} ${vb.w} ${vb.h}`}
        style={{ width: "100%", height: "100%", background: "#334155", cursor: tool === "edit" ? "grab" : "crosshair", touchAction: "none" }}
        data-testid="roof-svg" onPointerDownCapture={onPtrDownCap} onPointerMoveCapture={onPtrMoveCap} onPointerUpCapture={onPtrUpCap} onPointerCancelCapture={onPtrUpCap}
        onPointerDown={onDownBg} onPointerMove={onMove} onPointerUp={onUp} onContextMenu={(e) => e.preventDefault()}>
        <g ref={worldRef} transform={`rotate(${rot})`}>
        {imageUrl && <image href={imageUrl} x={-span / 2} y={-span / 2} width={span} height={span} preserveAspectRatio="none" style={{ imageRendering: "auto" }} />}

        {polys.map((p) => {
          const pts = p.verts.map((v) => `${v.x},${v.z}`).join(" ");
          const isSel = p.id === selId;
          return (
            <g key={p.id}>
              <polygon points={pts} fill={isSel ? "#22c55e22" : "#3b82f622"} stroke={isSel ? "#22c55e" : "#3b82f6"} strokeWidth={vb.w * 0.004}
                onPointerDown={(e) => { if (tool === "edit" || tool === "place") { e.stopPropagation(); setSelId(p.id); onDownBg(e); } }} onPointerUp={onUp} style={{ cursor: "pointer" }} />
              {p.verts.map((v, i) => {
                const n = p.verts[(i + 1) % p.verts.length];
                const len = Math.hypot(n.x - v.x, n.z - v.z);
                return (
                  <g key={`e${i}`}>
                    {isSel && tool === "edit" && (
                      <line x1={v.x} y1={v.z} x2={n.x} y2={n.z} stroke="transparent" strokeWidth={vb.w * 0.02} onPointerDown={(e) => onEdgeDown(e, p.id, i)} style={{ cursor: "copy" }} />
                    )}
                    <text x={(v.x + n.x) / 2} y={(v.z + n.z) / 2} transform={`rotate(${-rot} ${(v.x + n.x) / 2} ${(v.z + n.z) / 2})`} fontSize={fontSize} fill="#fde047" stroke="#000" strokeWidth={fontSize * 0.16} paintOrder="stroke" textAnchor="middle" dominantBaseline="middle" style={{ pointerEvents: "none", fontWeight: 700 }}>{fmtCm(len)}</text>
                  </g>
                );
              })}
              {isSel && tool === "edit" && p.verts.map((v, i) => (
                <circle key={`h${i}`} cx={v.x} cy={v.z} r={handleR} fill="#fff" stroke="#22c55e" strokeWidth={handleR * 0.4}
                  onPointerDown={(e) => onVertexDown(e, p.id, i)} onDoubleClick={(e) => { e._idx = i; onVertexDbl(e, p.id); }} style={{ cursor: "move" }} />
              ))}
            </g>
          );
        })}

        {/* disegno falda in corso */}
        {tool === "draw" && drawPts.length > 0 && (
          <g>
            <polyline points={drawPts.map((v) => `${v.x},${v.z}`).join(" ")} fill="#f59e0b22" stroke="#f59e0b" strokeWidth={vb.w * 0.004} />
            {drawPts.slice(0, -1).map((v, i) => {
              const n = drawPts[i + 1];
              const len = Math.hypot(n.x - v.x, n.z - v.z);
              return (
                <text key={`dl${i}`} x={(v.x + n.x) / 2} y={(v.z + n.z) / 2} transform={`rotate(${-rot} ${(v.x + n.x) / 2} ${(v.z + n.z) / 2})`} fontSize={fontSize} fill="#fbbf24" stroke="#000" strokeWidth={fontSize * 0.16} paintOrder="stroke" textAnchor="middle" dominantBaseline="middle" style={{ pointerEvents: "none", fontWeight: 700 }}>{fmtCm(len)}</text>
              );
            })}
            {drawPts.length >= 2 && (() => {
              const a = drawPts[drawPts.length - 1], b = drawPts[0];
              const len = Math.hypot(b.x - a.x, b.z - a.z);
              return (
                <g>
                  <line x1={a.x} y1={a.z} x2={b.x} y2={b.z} stroke="#f59e0b" strokeWidth={vb.w * 0.003} strokeDasharray={`${vb.w * 0.01} ${vb.w * 0.01}`} style={{ pointerEvents: "none" }} />
                  <text x={(a.x + b.x) / 2} y={(a.z + b.z) / 2} transform={`rotate(${-rot} ${(a.x + b.x) / 2} ${(a.z + b.z) / 2})`} fontSize={fontSize} fill="#fbbf24" stroke="#000" strokeWidth={fontSize * 0.16} paintOrder="stroke" textAnchor="middle" dominantBaseline="middle" style={{ pointerEvents: "none", fontWeight: 700 }}>{fmtCm(len)}</text>
                </g>
              );
            })()}
            {drawPts.map((v, i) => <circle key={i} cx={v.x} cy={v.z} r={handleR} fill={i === 0 ? "#f59e0b" : "#fff"} stroke="#f59e0b" strokeWidth={handleR * 0.4} />)}
          </g>
        )}

        {/* moduli */}
        {panels.map((pn, i) => {
          const isSel = i === selPanel && tool === "panel";
          const a = (pn.az || 0) * DEG;
          const hx = pn.x - Math.sin(a) * (dCos / 2 + 1.4);
          const hz = pn.z + Math.cos(a) * (dCos / 2 + 1.4); // handle "sopra" nel frame locale
          return (
            <g key={i}>
              <rect x={-panelW / 2} y={-dCos / 2} width={panelW} height={dCos} transform={`translate(${pn.x} ${pn.z}) rotate(${pn.az || 0})`}
                fill={isSel ? "#2563eb" : "#1e3a8a"} stroke={isSel ? "#fde047" : "#60a5fa"} strokeWidth={vb.w * (isSel ? 0.004 : 0.0025)}
                onPointerDown={tool === "panel" ? (e) => onPanelDown(e, i) : undefined} style={{ cursor: tool === "panel" ? "move" : "default" }} />
              {isSel && (
                <g>
                  <line x1={pn.x} y1={pn.z} x2={hx} y2={hz} stroke="#fde047" strokeWidth={vb.w * 0.003} style={{ pointerEvents: "none" }} />
                  <circle cx={hx} cy={hz} r={handleR * 1.1} fill="#fde047" stroke="#000" strokeWidth={handleR * 0.2} onPointerDown={(e) => onRotDown(e, i)} style={{ cursor: "grab" }} />
                </g>
              )}
            </g>
          );
        })}
        </g>
      </svg>

      {!showView && !mobile && (
        <button onClick={() => setShowView(true)} className="absolute right-2 bottom-2 md:bottom-auto md:top-2 z-[6] btn-secondary !px-2" style={{ background: "#ffffffeb" }} data-testid="roof-view-open">
          <Compass className="w-4 h-4" style={{ color: "#B30000", transform: `rotate(${rot}deg)` }} /> {rot}°
        </button>
      )}
      {showView && (
      <div className="absolute right-2 bottom-2 md:bottom-auto md:top-2 z-[6] w-[176px] md:w-[190px] p-2 rounded-[10px] max-md:!bottom-auto max-md:top-11 max-md:left-2 max-md:right-auto" style={{ background: "#ffffffeb" }} data-testid="roof-view-controls">
        <div className="flex items-center gap-2 mb-1.5">
          <div title="Nord" style={{ transform: `rotate(${rot}deg)`, transition: "transform .2s ease" }} data-testid="roof-compass">
            <Compass className="w-5 h-5" style={{ color: "#B30000" }} />
          </div>
          <span className="text-xs font-bold flex-1 whitespace-nowrap" data-testid="roof-rot-value">{rot}°</span>
          <button onClick={() => zoomBtn(0.75)} className="p-1 rounded hover:bg-black/5" title="Zoom +" data-testid="roof-zoom-in"><ZoomIn className="w-4 h-4" /></button>
          <button onClick={() => zoomBtn(1.33)} className="p-1 rounded hover:bg-black/5" title="Zoom −" data-testid="roof-zoom-out"><ZoomOut className="w-4 h-4" /></button>
          <button onClick={() => setShowView(false)} className="p-1 rounded hover:bg-black/5" title="Chiudi" data-testid="roof-view-close"><X className="w-4 h-4" /></button>
        </div>
        <input type="range" min={-180} max={180} step={1} value={rot} onChange={(e) => setRot(Number(e.target.value))} className="w-full" data-testid="roof-rot-slider" />
        <div className="flex gap-1 mt-1">
          <button onClick={() => setRot((r) => norm(r - 15))} className="btn-secondary text-xs flex-1 justify-center !px-1" data-testid="roof-rot-ccw"><RotateCcw className="w-3.5 h-3.5" /> 15°</button>
          <button onClick={() => setRot((r) => norm(r + 15))} className="btn-secondary text-xs flex-1 justify-center !px-1" data-testid="roof-rot-cw"><RotateCw className="w-3.5 h-3.5" /> 15°</button>
          <button onClick={() => setRot(0)} className="btn-secondary text-xs !px-2" title="Nord in alto" data-testid="roof-rot-reset">N</button>
          <button onClick={() => setVb({ x: -span / 2, y: -span / 2, w: span, h: span })} className="btn-secondary text-xs !px-2" title="Centra la casa" data-testid="roof-view-reset"><Crosshair className="w-3.5 h-3.5" /></button>
        </div>
        <button onClick={alignToFalda} className="btn-primary text-xs w-full justify-center mt-1" data-testid="roof-align-falda"><AlignHorizontalJustifyCenter className="w-3.5 h-3.5" /> Allinea alla falda</button>
      </div>
      )}

      <CompassRose rotation={rot} faldaAz={faldaAz} lat={lat} lng={lng} />

      <div className="absolute bottom-2 left-2 z-[4] max-w-[calc(100%-110px)] md:max-w-[260px] max-h-[72px] md:max-h-[180px]" style={{ background: "#ffffffe6", borderRadius: 8, padding: "6px 10px", overflowY: "auto", fontSize: 12, ...(mobile ? { bottom: ctxBar ? 132 : 84, maxWidth: "calc(100% - 72px)", maxHeight: 60, fontSize: 11 } : {}) }} data-testid="roof-measures">
        <div style={{ fontWeight: 700, marginBottom: 4 }}>Misure tetto</div>
        {perFalda.map((f) => (
          <div key={f.id} onClick={() => setSelId(f.id)} style={{ cursor: "pointer", padding: "1px 0", color: f.id === selId ? "var(--primary)" : "#334155", fontWeight: f.id === selId ? 700 : 400 }} data-testid={`roof-falda-${f.i}`}>
            Falda {f.i + 1}: {f.area.toFixed(1)} m² · {f.count} moduli
          </div>
        ))}
        <div style={{ borderTop: "1px solid #e2e8f0", marginTop: 4, paddingTop: 4, fontWeight: 700 }}>
          Totale: {perFalda.reduce((s, f) => s + f.area, 0).toFixed(1)} m² · {panels.length} moduli
        </div>
      </div>
    </div>
  );
}
